import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Nav,
  Row,
  Spinner,
  Tab,
} from "@themesberg/react-bootstrap";
import {
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiCreditCard,
  FiTrendingUp,
  FiDollarSign,
  FiSearch,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";
import { getUserMyTeam } from "../../api";
import { Routes } from "../../../routes";
import MyIds from "./MyIds/index";
import "./MyIds/myIds.css";
import "./userMyTeam.css";

const TAB_KEYS = {
  overview: "overview",
  my_ids: "my_ids",
  team: "team",
  active: "active",
  inactive: "inactive",
};

const AVATAR_COLORS = [
  "#1B3C6E",
  "#2E7D32",
  "#00838F",
  "#6A1B9A",
  "#F57C00",
  "#1565C0",
];

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  let hash = 0;
  const str = String(name || "");
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const statusBadge = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return <Badge bg="success">Active</Badge>;
  if (s === "blocked") return <Badge bg="danger">Blocked</Badge>;
  return <Badge bg="secondary">Inactive</Badge>;
};

const purchaseBadge = (status) => {
  const purchased = String(status || "").toLowerCase() === "purchased";
  return purchased ? (
    <Badge bg="success">Purchased</Badge>
  ) : (
    <Badge bg="secondary">Not Purchased</Badge>
  );
};

const typeBadge = (type) => {
  switch (type) {
    case "direct_member":
      return <Badge bg="primary">Direct Member</Badge>;
    case "sub_id_downline":
      return <Badge bg="info">Sub-ID Downline</Badge>;
    default:
      return <Badge bg="dark">Downline Member</Badge>;
  }
};

const typeLabel = (type) => {
  switch (type) {
    case "direct_member":
      return "Direct Member";
    case "sub_id_downline":
      return "Sub-ID Downline";
    default:
      return "Downline Member";
  }
};

const SummaryCard = ({ icon: Icon, label, value, tone = "navy" }) => (
  <div className={`my-team-summary-card tone-${tone}`}>
    <div className="my-team-summary-icon">
      <Icon size={18} />
    </div>
    <div>
      <div className="text-muted small">{label}</div>
      <div className="fw-bold fs-5">{value}</div>
    </div>
  </div>
);

const EmptyBlock = ({ message }) => (
  <div className="my-team-empty text-center text-muted py-5">{message}</div>
);

const MemberCard = ({ row, potentialLabel = "Potential" }) => {
  const monthIncome =
    row.current_month_income_generated ?? row.current_month_income ?? 0;

  return (
    <div className="my-team-member-card">
      <div className="my-team-member-card-header">
        <div
          className="my-team-avatar"
          style={{ backgroundColor: getAvatarColor(row.name) }}
        >
          {getInitials(row.name)}
        </div>
        <div className="my-team-member-card-title">
          <h6 className="text-truncate">{row.name || "—"}</h6>
          <div className="meta-id">{row.custom_user_id || "—"}</div>
          {row.username ? (
            <div className="meta-id">@{row.username}</div>
          ) : null}
          <div className="my-team-badge-row">
            {row.type ? typeBadge(row.type) : null}
            {statusBadge(row.status)}
            {purchaseBadge(row.course_purchase_status)}
          </div>
        </div>
      </div>

      <div className="my-team-stat-grid">
        <div className="my-team-stat">
          <span className="label">Level</span>
          <span className="value">
            {row.level ?? row.placement_level ?? "—"}
          </span>
        </div>
        <div className="my-team-stat">
          <span className="label">Direct / Downline</span>
          <span className="value">
            {row.direct_referrals_count ?? 0} / {row.downline_count ?? 0}
          </span>
        </div>
        <div className="my-team-stat">
          <span className="label">Month Income</span>
          <span className="value">{formatCurrency(monthIncome)}</span>
        </div>
        <div className="my-team-stat highlight">
          <span className="label">{potentialLabel}</span>
          <span className="value">
            {formatCurrency(row.potential_monthly_income)}
          </span>
        </div>
      </div>

      <div className="my-team-member-card-footer">
        <span>
          {row.parent_name
            ? `Parent: ${row.parent_name}`
            : row.placed_under?.name
              ? `Placed under: ${row.placed_under.name}`
              : row.type
                ? typeLabel(row.type)
                : "My ID"}
        </span>
        <span>{row.parent_custom_user_id || row.placed_under?.custom_user_id || ""}</span>
      </div>
    </div>
  );
};

const filterRows = (rows, search, statusFilter, levelFilter) => {
  const q = String(search || "").trim().toLowerCase();
  return (rows || []).filter((row) => {
    if (statusFilter) {
      const s = String(row.status || "").toLowerCase();
      if (statusFilter === "active" && s !== "active") return false;
      if (statusFilter === "inactive" && s === "active") return false;
    }
    if (levelFilter && String(row.level) !== String(levelFilter)) return false;
    if (!q) return true;
    const hay = [
      row.name,
      row.custom_user_id,
      row.username,
      row.parent_custom_user_id,
      row.parent_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
};

export default function UserMyTeam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || TAB_KEYS.overview;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [teamSearch, setTeamSearch] = useState("");
  const [teamStatus, setTeamStatus] = useState("");
  const [teamLevel, setTeamLevel] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [inactiveSearch, setInactiveSearch] = useState("");

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUserMyTeam();
      setData(response?.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load My Team");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && Object.values(TAB_KEYS).includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabSelect = (key) => {
    setActiveTab(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const summary = data?.summary || {};
  const meta = data?.meta || {};
  const helperText =
    meta.potential_income_helper ||
    "Potential income is an estimate based on all downline users and configured commission levels. Actual earnings depend on course purchases, opt-outs, direct referral eligibility, and final commission processing.";

  const teamMembers = useMemo(
    () => filterRows(data?.team_members || [], teamSearch, teamStatus, teamLevel),
    [data?.team_members, teamSearch, teamStatus, teamLevel]
  );

  const activeIds = useMemo(
    () => filterRows(data?.active?.ids || [], activeSearch, "", ""),
    [data?.active?.ids, activeSearch]
  );
  const activeUsers = useMemo(
    () => filterRows(data?.active?.users || [], activeSearch, "", ""),
    [data?.active?.users, activeSearch]
  );
  const inactiveIds = useMemo(
    () => filterRows(data?.inactive?.ids || [], inactiveSearch, "", ""),
    [data?.inactive?.ids, inactiveSearch]
  );
  const inactiveUsers = useMemo(
    () => filterRows(data?.inactive?.users || [], inactiveSearch, "", ""),
    [data?.inactive?.users, inactiveSearch]
  );

  const levelOptions = useMemo(() => {
    const levels = new Set((data?.team_members || []).map((m) => m.level).filter(Boolean));
    return Array.from(levels).sort((a, b) => a - b);
  }, [data?.team_members]);

  const renderMemberCards = (rows, emptyMessage, options = {}) => {
    if (!rows.length) return <EmptyBlock message={emptyMessage} />;

    return (
      <div className="my-team-card-grid">
        {rows.map((row) => (
          <MemberCard
            key={row.user_id || row.custom_user_id}
            row={row}
            potentialLabel={options.potentialLabel || "Potential"}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="my-team-page py-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h2 className="h4 mb-1">My Team</h2>
          <p className="text-muted mb-0 small">
            Manage your IDs, team members, active users, inactive users, and potential income.
          </p>
        </div>
        <Button
          className="my-team-btn-outline"
          size="sm"
          onClick={fetchTeam}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {meta.commission_config_missing && (
        <div className="my-team-alert warning mb-3">
          <FiAlertCircle className="me-2" />
          {meta.commission_config_message ||
            "Commission configuration for this month is not set."}
        </div>
      )}

      <div className="my-team-helper mb-4">
        <FiInfo className="me-2 flex-shrink-0 mt-1" />
        <span>{helperText}</span>
      </div>

      {loading && !data ? (
        <div className="text-center py-5 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading My Team...
        </div>
      ) : (
        <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
          <div className="my-team-tabs-wrap">
            <Nav className="my-team-tabs">
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.overview}>Overview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.my_ids}>My IDs</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.team}>Team</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.active}>Active</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.inactive}>Inactive</Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          <Tab.Content>
            <Tab.Pane eventKey={TAB_KEYS.overview}>
              <div className="my-team-summary-grid mb-4">
                <SummaryCard icon={FiCreditCard} label="Total My IDs" value={summary.total_my_ids ?? 0} />
                <SummaryCard
                  icon={FiUserCheck}
                  label="Active IDs"
                  value={summary.active_ids ?? 0}
                  tone="green"
                />
                <SummaryCard
                  icon={FiUserX}
                  label="Inactive IDs"
                  value={summary.inactive_ids ?? 0}
                  tone="amber"
                />
                <SummaryCard icon={FiUsers} label="Total Team" value={summary.total_team_members ?? 0} />
                <SummaryCard
                  icon={FiUserCheck}
                  label="Active Team"
                  value={summary.active_team_members ?? 0}
                  tone="green"
                />
                <SummaryCard
                  icon={FiUserX}
                  label="Inactive Team"
                  value={summary.inactive_team_members ?? 0}
                  tone="amber"
                />
                <SummaryCard
                  icon={FiDollarSign}
                  label="Current Month Income"
                  value={formatCurrency(summary.current_month_income)}
                  tone="green"
                />
                <SummaryCard
                  icon={FiTrendingUp}
                  label="Potential Monthly Income"
                  value={formatCurrency(summary.potential_monthly_income)}
                  tone="navy"
                />
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <Card className="my-team-quick-card border-0">
                    <Card.Body>
                      <h6 className="my-team-section-title mb-3">Quick links</h6>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="my-team-btn-primary"
                          onClick={() => handleTabSelect(TAB_KEYS.my_ids)}
                        >
                          Open My IDs
                        </Button>
                        <Button
                          size="sm"
                          className="my-team-btn-outline"
                          onClick={() => handleTabSelect(TAB_KEYS.team)}
                        >
                          View Team
                        </Button>
                        <Button
                          as={Link}
                          to={Routes.UserLevelNetworkPage.path}
                          size="sm"
                          className="my-team-btn-outline"
                        >
                          Network View
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="my-team-quick-card border-0">
                    <Card.Body>
                      <h6 className="my-team-section-title mb-2">Potential Monthly Income</h6>
                      <p className="text-muted small mb-0">{helperText}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane>

            <Tab.Pane eventKey={TAB_KEYS.my_ids}>
              <MyIds />
            </Tab.Pane>

            <Tab.Pane eventKey={TAB_KEYS.team}>
              <div className="my-team-filter-bar">
                <div className="row g-2 align-items-stretch">
                  <div className="col-12 col-lg-6">
                    <InputGroup>
                      <InputGroup.Text className="bg-white">
                        <FiSearch className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search by name, ID, username"
                        value={teamSearch}
                        onChange={(e) => setTeamSearch(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                  <div className="col-6 col-lg-3">
                    <Form.Select value={teamStatus} onChange={(e) => setTeamStatus(e.target.value)}>
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </div>
                  <div className="col-6 col-lg-3">
                    <Form.Select value={teamLevel} onChange={(e) => setTeamLevel(e.target.value)}>
                      <option value="">All Levels</option>
                      {levelOptions.map((lvl) => (
                        <option key={lvl} value={lvl}>
                          Level {lvl}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              </div>
              {renderMemberCards(teamMembers, "No team members found yet.")}
            </Tab.Pane>

            <Tab.Pane eventKey={TAB_KEYS.active}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h6 className="my-team-section-title mb-0">Active IDs</h6>
                <InputGroup style={{ maxWidth: 320 }}>
                  <InputGroup.Text className="bg-white">
                    <FiSearch className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search active..."
                    value={activeSearch}
                    onChange={(e) => setActiveSearch(e.target.value)}
                  />
                </InputGroup>
              </div>
              {renderMemberCards(activeIds, "No active IDs found.")}

              <h6 className="my-team-section-title mt-4">Active Users</h6>
              {renderMemberCards(activeUsers, "No active users found.")}
            </Tab.Pane>

            <Tab.Pane eventKey={TAB_KEYS.inactive}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h6 className="my-team-section-title mb-0">Inactive IDs</h6>
                <InputGroup style={{ maxWidth: 320 }}>
                  <InputGroup.Text className="bg-white">
                    <FiSearch className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search inactive..."
                    value={inactiveSearch}
                    onChange={(e) => setInactiveSearch(e.target.value)}
                  />
                </InputGroup>
              </div>
              {renderMemberCards(inactiveIds, "No inactive IDs found.", {
                potentialLabel: "Potential if Activated",
              })}

              <h6 className="my-team-section-title mt-4">Inactive Users</h6>
              {renderMemberCards(inactiveUsers, "No inactive users found.", {
                potentialLabel: "Potential if Activated",
              })}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}
    </div>
  );
}
